import React, { useCallback } from 'react';
import { Navigate, Outlet, Route, RouteObject, Routes, useLocation } from 'react-router-dom';
import authService from 'service/authService';
import * as ActionHistoryPage from '../domain/account/action-history';
import * as ContentProviderPage from '../domain/account/content-provider';
import * as LoginPage from '../domain/account/login';
import * as ProfilePage from '../domain/account/profile';
import * as UserPage from '../domain/account/user';
import * as ContentPage from '../domain/content';
import * as ClipTrailerPage from '../domain/content/clip-trailer';
import * as EpisodePage from '../domain/content/episode';
import * as GenrePage from '../domain/content/genre';
import * as GlobalSettingPage from '../domain/content/global-settings';
import * as LibraryPage from '../domain/content/library';
import * as MoviePage from '../domain/content/movie';
import * as PeoplePage from '../domain/content/people';
import * as SeasonPage from '../domain/content/season';
import * as Season2Page from '../domain/content/season2';
import * as SeriesPage from '../domain/content/series';
import * as Series2Page from '../domain/content/series2';
import * as DebugPage from '../domain/debug';
import * as ToolsPage from '../domain/tools';
import * as IngestPage from '../domain/ingest';
import * as MonitorPage from '../domain/monitor';
import * as PermissionPage from '../domain/permission';
import * as RevenuePage from '../domain/revenue';
import * as UploadPage from '../domain/upload';
import * as VideoPage from '../domain/video';
import * as VideoSourcePage from '../domain/video/asset-source';
import { accountStore } from '../shared/store/accountStore';

type PermissionRoute = RouteObject & { menuAction: string; menu: string };

const makeRoute = (
  path: string,
  element: React.ReactNode,
  menu: string,
  menuAction: string
): PermissionRoute => {
  return {
    path,
    element,
    menu,
    menuAction,
  };
};

const AppRoutes: React.FC = () => {
  const ProtectedRoute: React.FC = () => {
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
      authService
        .isAuthenticated()
        .then(isAuth => {
          setIsAuthenticated(isAuth);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, []);

    if (isLoading) {
      return null; // 또는 로딩 컴포넌트
    }

    return isAuthenticated ? <Outlet /> : <Navigate to="/login" state={{ from: location }} replace />;
  };

  const contentRoutes: PermissionRoute[] = [
    makeRoute('/', <UploadPage.ImportUploadPage />, 'content', 'read'),
    makeRoute('/main', <UploadPage.ImportUploadPage />, 'content', 'read'),
    makeRoute('/library', <LibraryPage.LibraryPage />, 'content', 'read'),
    makeRoute('/playlists', <ContentPage.PlaylistPage />, 'content', 'read'),
    makeRoute('/playlists/edit/:id', <ContentPage.PlaylistEditPage />, 'content', 'edit'),
    makeRoute('/playlists/add', <ContentPage.PlaylistAddPage />, 'content', 'add'),
    makeRoute('/video/detail/:id', <LibraryPage.LibraryEditPage />, 'content', 'edit'),
    makeRoute('/video/detail/:id', <LibraryPage.LibraryEditPage />, 'content', 'edit'),
    makeRoute('/clip-trailer/detail/:id', <ClipTrailerPage.ClipTrailerDetail />, 'content', 'edit'),
    makeRoute('/movie', <MoviePage.Movie />, 'movie', 'read'),
    makeRoute('/movie/edit/:id', <MoviePage.MovieEdit />, 'movie', 'edit'),
    makeRoute('/movie/edit/new', <MoviePage.MovieEdit />, 'movie', 'add'),
    makeRoute('/episode/edit/:id', <EpisodePage.EpisodeEdit />, 'episode', 'edit'),
    makeRoute('/episode/edit/new', <EpisodePage.EpisodeEdit />, 'episode', 'add'),
  ];

  const uploadRoutes: PermissionRoute[] = [
    makeRoute('/import-upload', <UploadPage.ImportUploadPage />, 'import-upload', 'read'),
    makeRoute('/upload/video', <UploadPage.UploadVideoPage />, 'upload', 'read'),
    makeRoute('/video-source', <UploadPage.VideoSourcePage />, 'video-source', 'read'),
    makeRoute('/video-source/add', <UploadPage.VideoSourceAddPage />, 'video-source', 'add'),
    makeRoute('/video-source/edit/:id', <UploadPage.VideoSourceEditPage />, 'video-source', 'edit'),
    makeRoute(
      '/video-source/content',
      <UploadPage.VideoSourceContentListPage />,
      'video-source',
      'read'
    ),
    makeRoute('/video-source/odk', <UploadPage.VideoSourceODKPage />, 'video-source', 'read'),
  ];

  const userRoutes: PermissionRoute[] = [
    makeRoute('/user', <UserPage.UserPage />, 'user', 'read'),
    makeRoute('/user/add', <UserPage.UserAddPage />, 'user', 'add'),
    makeRoute('/user/edit/:id', <UserPage.UserEditPage />, 'user', 'edit'),
    makeRoute('/account', <ProfilePage.ProfilePage />, 'account', 'read'),
    makeRoute('/action-history', <ActionHistoryPage.ActionHistoryPage />, 'action-history', 'read'),
    makeRoute(
      '/content-provider',
      <ContentProviderPage.ContentProviderPage />,
      'content-provider',
      'read'
    ),
    makeRoute(
      '/content-provider/add',
      <ContentProviderPage.ContentProviderAddPage />,
      'content-provider',
      'add'
    ),
    makeRoute(
      '/content-provider/edit/:id',
      <ContentProviderPage.ContentProviderEditPage />,
      'content-provider',
      'edit'
    ),
  ];

  const metadataStructureRoutes: PermissionRoute[] = [
    makeRoute('/series', <SeriesPage.SeriesPage />, 'series', 'read'),
    makeRoute('/series/edit2/:id', <Series2Page.SeriesEdit2 />, 'series', 'edit'),
    makeRoute('/series/edit2/new', <Series2Page.SeriesEdit2 />, 'series', 'add'),
    makeRoute('/season', <SeasonPage.Season />, 'season', 'read'),
    makeRoute('/season/edit2/new', <Season2Page.SeasonEdit2 />, 'season', 'add'),
    makeRoute('/season/edit2/:id', <Season2Page.SeasonEdit2 />, 'season', 'edit'),
    makeRoute('/people', <PeoplePage.People />, 'people', 'read'),
    makeRoute('/people/add', <PeoplePage.PeopleAdd />, 'people', 'add'),
    makeRoute('/people/edit/:id', <PeoplePage.PeopleEdit />, 'people', 'edit'),
    makeRoute('/contract', <ContentPage.ContractPage />, 'contract', 'read'),
    makeRoute('/contract/add', <ContentPage.ContractAddPage />, 'contract', 'add'),
    makeRoute('/contract/edit/:id', <ContentPage.ContractEditPage />, 'contract', 'edit'),
    makeRoute('/category', <ContentPage.CategoryPage />, 'category', 'read'),
    makeRoute('/tag', <ContentPage.TagPage />, 'tag', 'read'),
    makeRoute('/tag/add', <ContentPage.TagAddPage />, 'tag', 'add'),
    makeRoute('/tag/edit/:id', <ContentPage.TagEditPage />, 'tag', 'edit'),
    makeRoute('/genre', <GenrePage.GenrePage />, 'genre', 'read'),
    makeRoute('/genre/add', <GenrePage.GenreAdd />, 'genre', 'add'),
    makeRoute('/genre/edit/:id', <GenrePage.GenreEdit />, 'genre', 'edit'),
    makeRoute('/global-setting', <GlobalSettingPage.GlobalSetting />, 'global-setting', 'read'),
  ];

  const revenueRoutes: PermissionRoute[] = [
    makeRoute('/revenue-share', <RevenuePage.RevenueSharePage />, 'revenue-share', 'read'),
    makeRoute(
      '/revenue-dashboard',
      <RevenuePage.RevenueDashboardPage />,
      'revenue-dashboard',
      'read'
    ),
  ];

  const permissionRoutes: PermissionRoute[] = [
    makeRoute('/role', <PermissionPage.RolePage />, 'role', 'read'),
    makeRoute('/role/add', <PermissionPage.RoleAdd />, 'role', 'add'),
    makeRoute('/role/edit/:id', <PermissionPage.RoleEdit />, 'role', 'edit'),
  ];

  // Platform에 따라 assets 경로를 리다이렉트하는 컴포넌트
  const AssetsRedirect: React.FC = () => {
    const account = accountStore.getAccount();
    const platformId = account?.platform?.id;

    // platform이 2이면 assets213으로 리다이렉트
    if (platformId === 2) {
      return <Navigate to="/assets213" replace />;
    }
    // platform이 1이거나 기타인 경우 기본 assets 페이지 표시
    return <VideoPage.AssetsPage />;
  };

  const videoRoutes: PermissionRoute[] = [
    makeRoute('/assets-source', <VideoSourcePage.VideoSourcePage />, 'assets-source', 'read'),
    makeRoute('/assets', <AssetsRedirect />, 'assets', 'read'),
    makeRoute('/assets/edit/:id', <VideoPage.AssetsEditPage />, 'assets', 'edit'),
    makeRoute('/assets/add', <VideoPage.AssetAddPage />, 'assets', 'add'),
    makeRoute('/assets213', <VideoPage.Assets213Page />, 'assets', 'read'),
  ];

  const syncRoutes: PermissionRoute[] = [
    makeRoute(
      '/video-sync-monitor',
      <IngestPage.VideoSyncMonitorListPage />,
      'video-sync-monitor',
      'read'
    ),
    makeRoute(
      '/video-sync-monitor/detail/:id',
      <IngestPage.VideoSyncMonitorDetailPage />,
      'video-sync-monitor',
      'read'
    ),
    makeRoute(
      '/video-sync-monitor/test',
      <IngestPage.VideoSyncMonitorTestPage />,
      'video-sync-monitor',
      'read'
    ),
    makeRoute(
      '/inhouse-sync-monitor',
      <MonitorPage.InhouseSyncMonitorListPage />,
      'inhouse-sync-monitor',
      'read'
    ),
    makeRoute(
      '/inhouse-sync-monitor/detail/:id',
      <MonitorPage.InhouseSyncMonitorDetailPage />,
      'inhouse-sync-monitor',
      'read'
    ),
    makeRoute(
      '/inhouse-sync-monitor/test',
      <MonitorPage.InhouseSyncMonitorTestPage />,
      'inhouse-sync-monitor',
      'read'
    ),
  ];

  const ingestRoutes: PermissionRoute[] = [
    makeRoute('/kcp-ingest', <IngestPage.KcpIngestListPage />, 'kcp-ingest', 'read'),
    makeRoute('/kcp-info', <IngestPage.KcpInfoListPage />, 'kcp-info', 'read'),
    makeRoute('/kcp-ingest/detail/:id', <IngestPage.KcpIngestDetailPage />, 'kcp-ingest', 'read'),
    makeRoute(
      '/mediahold-ingest',
      <IngestPage.MediaholdIngestListPage />,
      'mediahold-ingest',
      'read'
    ),
    makeRoute(
      '/mediahold-ingest/detail/:id',
      <IngestPage.MediaholdIngestDetailPage />,
      'mediahold-ingest',
      'read'
    ),
    makeRoute(
      '/title-auto-generator',
      <IngestPage.TitleAutoGeneratorListPage />,
      'title-auto-generator',
      'read'
    ),
    makeRoute(
      '/title-auto-generator/add',
      <IngestPage.TitleAutoGeneratorAddPage />,
      'title-auto-generator',
      'add'
    ),
    makeRoute(
      '/title-auto-generator/edit/:id',
      <IngestPage.TitleAutoGeneratorEditPage />,
      'title-auto-generator',
      'edit'
    ),
  ];

  const debugRoutes: PermissionRoute[] = [
    makeRoute('/debug', <DebugPage.DebugPage />, 'debug', 'read'),
    makeRoute('/content-relay', <ToolsPage.ContentRelayPage.ContentRelayPage />, 'debug', 'read'),
  ];

  const renderRoutes = useCallback(
    (routes: RouteObject[]) =>
      routes.map((route, index) => <Route key={index} path={route.path} element={route.element} />),
    []
  );

  return (
    <Routes>
      <Route path="/login" element={<LoginPage.LoginPage />} />
      <Route element={<ProtectedRoute />}>
        {renderRoutes(contentRoutes)}
        {renderRoutes(uploadRoutes)}
        {renderRoutes(userRoutes)}
        {renderRoutes(metadataStructureRoutes)}
        {renderRoutes(revenueRoutes)}
        {renderRoutes(permissionRoutes)}
        {renderRoutes(videoRoutes)}
        {renderRoutes(ingestRoutes)}
        {renderRoutes(debugRoutes)}
        {renderRoutes(syncRoutes)}
      </Route>
    </Routes>
  );
};

export default AppRoutes;
